import React from 'react';
import ManagerDashboardActions from 'actions/ManagerDashboardActions';
import connectToStores from 'alt-utils/lib/connectToStores';
import ManagerDashboardStore from 'stores/ManagerDashboardStore';
import {List, Map} from 'immutable';
import User, {displayName} from 'records/User';
import pick from 'lodash/pick';
import {userKeys} from 'sources/RepProfileSource';
import ConversationStyleTooltipContent from '$pages/$rep-profile/tooltips/ConversationStyleTooltipContent';
import QuadrantChart from 'components/pure/charts/QuadrantChart';
import {yScale} from '$pages/$rep-profile/RepProfileSummary';
import WidgetHeader from 'components/manager-dashboard/WidgetHeader';
import UserAvatar from 'components/pure/utils/UserAvatar';
import classNames from 'classnames';
import './ManagerCallMetrics.styl';
import DashV2MainWidgetLoader from 'components/dashboard/DashV2MainWidgetLoader';
import {getManagerIdIfNotAuthUser} from 'components/StatsPage2.utils';

const chartHeight = 500;
const widgetId = 'manager_call_metrics';

class ManagerCallMetrics extends React.Component {
  constructor(props) {
    super(props);
    this.onSelectRep = this.onSelectRep.bind(this);
    this.onHoverRep = this.onHoverRep.bind(this);
    this.onAddPoints = this.onAddPoints.bind(this);
    this.setChartRef = this.setChartRef.bind(this);
    this.userToPoint = Map();
    this.state = {
      hoveredRepId: null,
    };
  }

  static getStores() {
    return [ManagerDashboardStore];
  }

  static getPropsFromStores() {
    const store = ManagerDashboardStore.getState();
    const {widgets, widgetState} = store;
    return {widgets, widgetState};
  }

  componentDidMount() {
    const {startDate, endDate, selectedTeam} = this.props;
    this.getWidget(startDate, endDate, selectedTeam);
  }

  getWidget(startDate, endDate, selectedTeam) {
    if (!selectedTeam) return;
    const teamId = selectedTeam.id;
    const managerId = this.props.managerId;
    const widgetConfig = {
      teamId: teamId,
      managerId: managerId,
      teamName: selectedTeam.name,
      startDate: startDate,
      endDate: endDate,
      widgetId: widgetId,
      widgetType: 'MANAGER_CALL_METRICS',
    };

    ManagerDashboardActions.getWidget.defer([widgetConfig, getManagerIdIfNotAuthUser(this.props)]);
  }

  componentDidUpdate(prevProps) {
    const {startDate, endDate, selectedTeam} = this.props;
    const {
      startDate: prevStartDate,
      endDate: prevEndDate,
      selectedTeam: prevSelectedTeam,
    } = prevProps;
    if (
      startDate === prevStartDate &&
      endDate === prevEndDate &&
      ((!selectedTeam && !prevSelectedTeam) ||
        (selectedTeam && prevSelectedTeam && selectedTeam.id === prevSelectedTeam.id))
    ) {
      return;
    }
    this.getWidget(startDate, endDate, selectedTeam);
  }

  componentWillUnmount() {
    ManagerDashboardActions.reset();
  }

  onSelectRep(repId) {
    // no-op for now
  }

  onHoverRep(repId) {
    this.setState({hoveredRepId: repId});
  }

  onAddPoints(userToPoint) {
    if (userToPoint.size > 0 && !userToPoint.equals(this.userToPoint)) {
      // We are merging instead of replacing because QuadrantChart will call this function with points
      // that are currently not showing, so if say you go from viewing Team One to viewing All, then it is the users
      // that are not in Team One that will be in the userToPoint that is passed in
      const newMap = this.userToPoint ? this.userToPoint.merge(userToPoint) : userToPoint;
      this.userToPoint = newMap;
    }
  }

  sortUsers(rawUsers, withDataUserIds) {
    const decoratedAndSortedUsers = rawUsers
      .map(user =>
        Object.assign(user, {
          displayName: displayName(user),
          hasData: withDataUserIds.contains(user.id),
        })
      )
      .sort((u1, u2) => {
        if (u1.hasData && !u2.hasData) return -1;
        if (!u1.hasData && u2.hasData) return 1;
        return u1.displayName.toLowerCase().localeCompare(u2.displayName.toLowerCase());
      });
    return decoratedAndSortedUsers;
  }

  renderUsers(rawUsers, withDataUserIds) {
    const users = this.sortUsers(rawUsers, withDataUserIds);
    return (
      <div className="widget rep-list">
        {users.map(user => {
          const hoveredClass = user.id === this.state.hoveredRepId ? 'hovered' : '';
          const hasData = user.hasData;
          const disabledClass = !hasData ? 'disabled' : '';
          return (
            <div
              key={user.id}
              className={classNames('rep-list-row', hoveredClass, disabledClass)}
              onMouseOver={() => hasData && this.onHoverRep(user.id)}
              onMouseOut={() => hasData && this.onHoverRep(null)}>
              <UserAvatar user={user} enableTooltip={false} tooltipDirection="top" />
              {user.displayName}
            </div>
          );
        })}
      </div>
    );
  }

  setChartRef(ref) {
    this.chartRef = ref;
  }

  render() {
    if (!this.props.widgets) return null;
    const widget = this.props.widgets.get(widgetId);

    if (!widget) {
      return <DashV2MainWidgetLoader />;
    }

    const teamStats = List(widget.teamSummary);
    const users = List(widget.teamSummary.map(u => pick(u, userKeys)));
    const quadrantData = teamStats
      .filter(stats => stats.talkListenRatioAvg > 0 && stats.interchangesPerMinuteAvg > 0)
      .map(s => ({
        ...s,
        listenPercentage: s.talkListenRatioAvg * 100,
        user: new User(pick(s, userKeys)),
      }))
      .map(stats => ({
        id: stats.user.id,
        selected: stats.user.id === this.state.hoveredRepId,
        x: stats.listenPercentage,
        y: stats.interchangesPerMinuteAvg,
        label: (
          <ConversationStyleTooltipContent
            user={stats.user}
            listenPercentage={stats.listenPercentage}
            interchangesPerMinuteAvg={stats.interchangesPerMinuteAvg}
            onClick={() => this.onSelectRep(stats.user.id)}
          />
        ),
      }));
    const withDataUserIds = quadrantData.map(stats => stats.id).toSet();
    const pointToShow = this.state.hoveredRepId
      ? this.userToPoint.get(this.state.hoveredRepId)
      : null;
    const repPointInChart = this.chartRef && pointToShow && pointToShow.ref;
    return (
      <div className="standalone-widget-wrapper">
        <div className="widget manager-call-metrics-widget">
          <WidgetHeader {...this.props} showDates={true} title="Conversation Styles">
            &nbsp;
          </WidgetHeader>
          <div className="content call-metrics-content">
            <QuadrantChart
              rawData={widget}
              data={quadrantData}
              xDomain={[0, 100]}
              width={chartHeight}
              height={chartHeight}
              xNegativeLabel="Average Interchanges Per Minute"
              yNegativeLabel="Average Listen Percentage"
              yScale={yScale(quadrantData, chartHeight)}
              onAddPoints={this.onAddPoints}
              setChartRef={this.setChartRef}
              anchor={repPointInChart}
              hoveredEl={repPointInChart}
              content={pointToShow && pointToShow.content}
            />
            {this.renderUsers(users, withDataUserIds)}
          </div>
        </div>
      </div>
    );
  }
}

export default connectToStores(ManagerCallMetrics);
